import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@saviynt/design-system';
import cx from 'classnames';

import { SafeBoundary } from '../../../shared/components';
import {
  PERSONALIZATION_DEFAULT_CONFIG,
  RENDER_MODES,
  testIds,
  VIOLATIONS_INI_CALL,
  violationsHome,
} from '../../../shared/constants';
import {
  useEffectAfterMount,
  usePersonalization,
  useTypedSelector,
} from '../../../shared/hooks';
import {
  globalActions,
  violationsTabActions,
} from '../../../shared/store/actions';
import { createDevRejectionHandler } from '../../../shared/utils';
import getUsername from '../../../shared/utils/getUsername';
import { useLazyGetExportStatusQuery } from '../../ViolationsReport/data-layer/violationsReportApi';
import type {
  ExportStatusItem,
  GetExportStatusData,
} from '../../ViolationsReport/data-layer/violationsReportTypes';
import { violationsReportActions } from '../../ViolationsReport/data-store/violationsReportSlice';
import {
  useColumnPicker,
  useFiltersInitialization,
  useGetActionStatus,
  useGetSortMenu,
  usePageTracker,
  useSetRenderMode,
  useTabFilters,
  useWidgetInitialization,
} from '../hooks';

import ViolationsControls from './ViolationsControls';
import ViolationsInsights from './ViolationsInsights';
import { ViolationsListActionsManager } from './ViolationsListActions';
import ViolationsSidePanel from './ViolationsSidePanel';
import ViolationsTabFooter from './ViolationsTabFooter';
import ViolationsTabGraph from './ViolationsTabGraph';
import ViolationsTabList from './ViolationsTabList';
import ViolationsWidgets from './ViolationsWidgets';

import './ViolationsTab.css';

/**
 * ViolationsTab
 *
 * A component that manages and displays violations data in both list and graph modes.
 * It handles the initialization of filters, manages different view modes, and coordinates the display
 * of violations data along with related widgets and side panels.
 *
 * @returns {ReactElement} Returns a section containing violations data visualization
 *
 * @example
 * <ViolationsTab />
 *
 * @dependencies
 * - react
 * - react-redux
 * - @saviynt/design-system
 *
 * @features
 * - Supports both list and graph view modes
 * - Integrates with SodInsights component
 * - Handles advanced and tab filters initialization
 * - Manages side panel visibility
 * - Supports bulk actions with footer
 * - Includes violations widgets in graph mode
 *
 * @state
 * The component relies on the following Redux state:
 * - activeViolationsTab: Current active violations tab
 * - renderMode: Current view mode (list/graph)
 * - isInsightsOpen: Visibility state of insights panel
 * - isSidePanelVisible: Visibility state of side panel
 * - isFooterVisible: Visibility state of footer
 *
 * @hooks
 * - useDispatch: For dispatching Redux actions
 * - useInvertedLocalize: For localization
 * - useTypedSelector: For accessing Redux state
 * - useFiltersInitialization: For initializing advanced filters
 * - useTabFilters: For managing tab filters
 * - usePageTracker: For tracking page state
 * - useGetQueryMap: For handling query parameters
 */
function ViolationsTab(): ReactElement {
  const dispatch = useDispatch();
  const scrollRef = useRef<HTMLElement>(null);

  const {
    user: { isMenuPinned },
    aag: {
      global: { activeViolationsTab, isGlobalLoading },
      violationsTab: {
        controls: {
          renderMode,
          isInsightsOpen,
          sortInAction,
          tabInAction,
          activeFilters,
        },
        list: { isRefresh },
        bulkaction: { isFooterVisible },
        sidepanel: { isSidePanelVisible },
        actionManager,
      },
    },
  } = useTypedSelector((state) => state);

  // Ref so initializeTabFilters always reads the latest tabInAction without
  // being a dep of useEffectAfterMount. Adding tabInAction to those deps
  // re-fires the effect on every tab click while isRefresh=true, launching a
  // second concurrent initializeTabFilters call that races with the first and
  // can snap the visual tab selection back to the originally-active tab.
  const tabInActionRef = useRef<string | null>(null);

  tabInActionRef.current = tabInAction;

  // Initial query hooks
  const { updateTracker } = usePageTracker(Object.values(VIOLATIONS_INI_CALL));
  const { initializeAdvancedFilters } = useFiltersInitialization(activeFilters);
  const { initializeTabFilters } = useTabFilters();
  const { initializeViewColumns } = useColumnPicker();
  const { initializeActionStatus } = useGetActionStatus();

  // Enable sort hook
  useGetSortMenu(sortInAction);

  // Set default widgets
  useWidgetInitialization();

  // Set default render mode
  useSetRenderMode({ currentMode: renderMode });

  // Export status API
  const [getExportStatus] = useLazyGetExportStatusQuery();

  // Function to get current user ID from cookies
  const getCurrentUserId = useCallback((): string => getUsername(), []);

  // Personalization
  const { savePersonlization } = usePersonalization();

  useEffect(() => {
    if (!isGlobalLoading) {
      savePersonlization(PERSONALIZATION_DEFAULT_CONFIG);
    }
  }, [isGlobalLoading, savePersonlization]);

  // On page load
  useEffect(() => {
    if (activeViolationsTab === violationsHome) {
      const initializeFilters = async () => {
        // Order is important
        const response = await Promise.all([
          initializeAdvancedFilters(),
          initializeTabFilters(tabInAction),
          initializeViewColumns(),
          initializeActionStatus(),
        ]);

        // Raise global error is any one fails
        const isFalseValue = response.some((value) => !value);

        if (isFalseValue) {
          dispatch(globalActions.setGlobalError({ state: true }));
        }

        // Don't load page if untill all pass
        Object.values(VIOLATIONS_INI_CALL).forEach((callValue, index) => {
          updateTracker(callValue, response[index]);
        });
      };

      initializeFilters().catch(
        createDevRejectionHandler('ViolationsTab: initializeFilters')
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only `activeViolationsTab` should trigger this
  }, [dispatch, activeViolationsTab]);

  // Initial export status check on mount
  useEffect(() => {
    const checkInitialExportStatus = async () => {
      try {
        const userId = getCurrentUserId();

        const data: GetExportStatusData = await getExportStatus(
          userId
        ).unwrap();

        if (Array.isArray(data.exports)) {
          data.exports.forEach((exportItem: ExportStatusItem) => {
            const { reportType, status, exportId, progressPercentage } =
              exportItem;

            // Update progress so stacked success banners can render when 100%
            dispatch(
              violationsReportActions.setExportProgress({
                reportType,
                progressPercentage,
              })
            );

            if (status === 'PROCESSING' || status === 'QUEUED') {
              dispatch(
                violationsReportActions.addActiveExport({
                  reportType,
                  progress: progressPercentage,
                })
              );
            } else if (status === 'COMPLETED') {
              dispatch(
                violationsReportActions.setExportIdForReportType({
                  reportType,
                  exportId,
                  status,
                })
              );

              // dispatch(
              //   violationsReportActions.setExportBanner({
              //     isVisible: true,
              //     title: 'Export Completed',
              //     description: `${formatReportType(
              //       reportType
              //     )} export has completed successfully.`,
              //     colorTheme: 'Success',
              //     kind: 'Success',
              //     exportId,
              //   })
              // );
            } else if (status === 'FAILED') {
              dispatch(
                violationsReportActions.setExportIdForReportType({
                  reportType,
                  exportId,
                  status,
                })
              );
            }
          });
        }
      } catch (error) {
        // Fail silently as this is not critical for page functionality
      }
    };

    if (activeViolationsTab === violationsHome) {
      checkInitialExportStatus().catch(
        createDevRejectionHandler('ViolationsTab: checkInitialExportStatus')
      );
    }
  }, [activeViolationsTab, getExportStatus, getCurrentUserId, dispatch]);

  useEffectAfterMount(() => {
    if (isRefresh) {
      initializeTabFilters(tabInActionRef.current)
        .catch(createDevRejectionHandler('ViolationsTab: initializeTabFilters'))
        .finally(() => {
          dispatch(violationsTabActions.setListIsRefresh(false));
        });
    }
    // tabInAction intentionally excluded — use ref to read the latest value
    // without making the effect re-fire on every tab click while isRefresh=true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefresh]);

  const isGraphMode = useMemo(
    () => Boolean(renderMode === RENDER_MODES.GRAPH),
    [renderMode]
  );
  const isListMode = useMemo(
    () => Boolean(renderMode === RENDER_MODES.LIST),
    [renderMode]
  );
  const renderContent = useMemo(() => {
    if (isGlobalLoading) {
      return null;
    }

    if (isGraphMode) {
      return <ViolationsTabGraph />;
    }

    return (
      <>
        <ViolationsTabList />
        <ViolationsTabFooter />
      </>
    );
  }, [isGraphMode, isGlobalLoading]);

  //  Scroll to top when gone to list page after scrolling in graph mode
  useEffect(() => {
    if (isListMode) {
      scrollRef.current?.scrollTo(0, 0);
    }
  }, [isListMode]);

  const violationsTabClasses = useMemo(
    () => cx('Sod-ViolationsTab', isListMode && 'Sod-ViolationsTab--isList'),
    [isListMode]
  );
  const violationsTabViewClasses = useMemo(
    () =>
      cx(
        'Sod-ViolationsTab-view',
        isGraphMode && 'Sod-ViolationsTab-view--isGraph',
        isListMode && 'Sod-ViolationsTab-view--isList',
        isSidePanelVisible && 'Sod-ViolationsTab-view--isSidePanelVisible',
        isInsightsOpen && 'Sod-ViolationsTab-view--isInsightsOpen',
        isFooterVisible && 'Sod-ViolationsTab-view--isFooterVisible',
        !isSidePanelVisible &&
          !isInsightsOpen &&
          'Sod-ViolationsTab-view--isNotAsides',
        isMenuPinned && 'Sod-ViolationsTab-view--isMenuPinned'
      ),
    [
      isGraphMode,
      isListMode,
      isSidePanelVisible,
      isInsightsOpen,
      isFooterVisible,
      isMenuPinned,
    ]
  );

  return (
    <section
      ref={scrollRef}
      className={violationsTabClasses}
      data-testid={testIds.SOD_VIOLATIONS_PAGE_BODY}>
      <SafeBoundary>
        <Box
          className={violationsTabViewClasses}
          dataTheme={isGraphMode ? 'dark' : undefined}>
          <Box className='Sod-ViolationsView-wrapper'>
            <Box className='Sod-ViolationsView-content'>
              <ViolationsControls />
              {renderContent}
            </Box>
            <Box className='Sod-ViolationsView-asides'>
              <ViolationsInsights className='Sod-ViolationsView-asidesInsight' />
              <ViolationsSidePanel />
            </Box>
          </Box>
        </Box>
        {isGraphMode && <ViolationsWidgets />}
        {/* Actions */}
        {actionManager?.isOpen && (
          <ViolationsListActionsManager
            // eslint-disable-next-line react/jsx-props-no-spreading -- need to pass all the action properties
            {...actionManager}
          />
        )}
      </SafeBoundary>
    </section>
  );
}

export default memo(ViolationsTab);
